import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { evaluationAPI, categoryAPI, projectAPI } from '../../api/services';
import { Category, CategoryScore, Evaluation, User, TaggedProject, Project } from '../../types';
import { RootState } from '../../store';
import { selectAllEvaluations, setCurrentEvaluation, updateEvaluation } from '../../store/evaluationsSlice';

const EditEvaluationWithAdminScore: React.FC = () => {
  const dispatch = useDispatch();
  const allEvaluations = useSelector(selectAllEvaluations);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryScores, setCategoryScores] = useState<CategoryScore[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showAdminScoresToEmployee, setShowAdminScoresToEmployee] = useState(false);
  const [taggedProjects, setTaggedProjects] = useState<TaggedProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [conflictDetected, setConflictDetected] = useState(false);
  const [lastKnownEmployeeUpdate, setLastKnownEmployeeUpdate] = useState<string | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (id) {
      // First check if evaluation is in Redux cache
      const cachedEvaluation = allEvaluations.find(e => e._id === id);

      console.log('cachedEvaluation:', cachedEvaluation);
      if (cachedEvaluation) {
        // Use cached version
        setEvaluation(cachedEvaluation);
        dispatch(setCurrentEvaluation(cachedEvaluation));

        // Normalize scores from cached data
        const normalizedScores = cachedEvaluation.categoryScores.map((cat: CategoryScore) => ({
          ...cat,
          scores: cat.scores.map(s => ({
            ...s,
            score: typeof s.score === 'number' ? s.score : (s.score ? parseFloat(String(s.score)) : 1),
            adminScore: s.adminScore && typeof s.adminScore !== 'number'
              ? parseFloat(String(s.adminScore))
              : s.adminScore,
          })),
          totalWeightedScore: typeof cat.totalWeightedScore === 'number'
            ? cat.totalWeightedScore
            : (cat.totalWeightedScore ? parseFloat(String(cat.totalWeightedScore)) : 0),
          adminTotalWeightedScore: cat.adminTotalWeightedScore && typeof cat.adminTotalWeightedScore !== 'number'
            ? parseFloat(String(cat.adminTotalWeightedScore))
            : cat.adminTotalWeightedScore,
        }));

        setCategoryScores(normalizedScores);
        setShowAdminScoresToEmployee(cachedEvaluation.showAdminScoresToEmployee || false);
        setTaggedProjects(cachedEvaluation.taggedProjects || []);

        // Initialize tracking for concurrent editing
        if (cachedEvaluation.lastUpdatedBy === 'employee') {
          setLastKnownEmployeeUpdate(cachedEvaluation.updatedAt);
        }

        // Fetch categories and reorder scores to match
        fetchCategoriesAndReorderScores(cachedEvaluation.levelGroup, normalizedScores, cachedEvaluation.evaluationTypeId);
      } else {

        fetchEvaluation(id);
      }
    }
  }, [id, allEvaluations]);

  const refreshEvaluation = useCallback(async () => {
    if (!id) return;

    setIsRefreshing(true);
    try {
      const response = await evaluationAPI.getEvaluationById(id);
      const freshEvaluation = response.data;

      // Update local state with fresh data
      setEvaluation(freshEvaluation);
      dispatch(setCurrentEvaluation(freshEvaluation));

      // Only update employee scores and evidence, preserve admin work
      // If no existing categoryScores, this is initial load - use fresh data

      console.log('[ADMIN REFRESH] Fetched fresh evaluation data for refresh:', freshEvaluation);
      console.log('[ADMIN REFRESH] Current categoryScores before refresh:', categoryScores);

      const updatedScores = categoryScores.length === 0
        ? freshEvaluation.categoryScores.map((cat: CategoryScore) => ({
          ...cat,
          scores: cat.scores.map(s => ({
            ...s,
            score: typeof s.score === 'number' ? s.score : (s.score ? parseFloat(String(s.score)) : 1),
            adminScore: s.adminScore && typeof s.adminScore !== 'number'
              ? parseFloat(String(s.adminScore))
              : s.adminScore,
          })),
          totalWeightedScore: typeof cat.totalWeightedScore === 'number'
            ? cat.totalWeightedScore
            : (cat.totalWeightedScore ? parseFloat(String(cat.totalWeightedScore)) : 0),
          adminTotalWeightedScore: cat.adminTotalWeightedScore && typeof cat.adminTotalWeightedScore !== 'number'
            ? parseFloat(String(cat.adminTotalWeightedScore))
            : cat.adminTotalWeightedScore,
        }))
        : categoryScores.map((currentCat) => {
          const freshCat = freshEvaluation.categoryScores.find(
            (cat: CategoryScore) => cat.categoryName === currentCat.categoryName
          );

          if (freshCat) {
            return {
              ...currentCat,
              // Update employee scores and evidence only
              scores: currentCat.scores.map((currentScore, index) => {
                const freshScore = freshCat.scores.find(
                  (s: any) => s.subCategoryName === currentScore.subCategoryName
                );

                if (freshScore) {
                  return {
                    ...currentScore,
                    score: typeof freshScore.score === 'number' ? freshScore.score : (freshScore.score ? parseFloat(String(freshScore.score)) : 1),
                    evidence: freshScore.evidence || '',
                    // Preserve admin data - prioritize current state to keep unsaved changes
                    adminScore: currentScore.adminScore ?? freshScore.adminScore,
                    adminComments: currentScore.adminComments ?? freshScore.adminComments,
                  };
                }
                return currentScore;
              }),
              // Update employee total score only
              totalWeightedScore: typeof freshCat.totalWeightedScore === 'number'
                ? freshCat.totalWeightedScore
                : (freshCat.totalWeightedScore ? parseFloat(String(freshCat.totalWeightedScore)) : 0),
              // Preserve admin total score - use current if exists, otherwise fresh
              adminTotalWeightedScore: currentCat.adminTotalWeightedScore ?? (
                freshCat.adminTotalWeightedScore && typeof freshCat.adminTotalWeightedScore !== 'number'
                  ? parseFloat(String(freshCat.adminTotalWeightedScore))
                  : freshCat.adminTotalWeightedScore
              ),
            };
          }
          return currentCat;
        });

      console.log('Fresh evaluation data from refresh:', freshEvaluation);

      console.log('[ADMIN REFRESH] ✓ Preserved admin work during refresh:', {
        categoriesUpdated: updatedScores.length,
        hasAdminScores: updatedScores.some((cat: CategoryScore) => cat.scores.some((s: any) => s.adminScore !== undefined)),
        hasAdminComments: updatedScores.some((cat: CategoryScore) => cat.scores.some((s: any) => s.adminComments !== undefined && s.adminComments !== ''))
      });

      // Detect conflicts: if employee updated after we last knew
      if (freshEvaluation.lastUpdatedBy === 'employee' &&
        freshEvaluation.updatedAt !== lastKnownEmployeeUpdate) {
        if (lastKnownEmployeeUpdate !== null) {
          setConflictDetected(true);
          setTimeout(() => setConflictDetected(false), 5000); // Hide after 5 seconds
        }
        setLastKnownEmployeeUpdate(freshEvaluation.updatedAt);
      }

      setCategoryScores(updatedScores);
      setShowAdminScoresToEmployee(freshEvaluation.showAdminScoresToEmployee || false);
      setTaggedProjects(freshEvaluation.taggedProjects || []);
      setLastRefreshed(new Date());

      console.log('[ADMIN REFRESH] ✓ Evaluation refreshed successfully');
    } catch (error) {
      console.error('[ADMIN REFRESH] Error refreshing evaluation:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [id, dispatch, categoryScores, lastKnownEmployeeUpdate]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefreshEnabled || !id) return;

    const interval = setInterval(() => {
      refreshEvaluation();
    }, 10000); // Refresh every 10 seconds

    refreshIntervalRef.current = interval;

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefreshEnabled, refreshEvaluation, id]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const reorderScoresToMatchCategories = (categories: any[], scores: CategoryScore[]) => {
    const reorderedScores = categories.map(category => {
      const match = scores.find(score => score.categoryName === category.name);
      if (match) return match;
      return {
        categoryName: category.name,
        scores: category.subCategories.map((sub: any) => ({
          subCategoryName: sub.name,
          score: 1,
          evidence: '',
          adminScore: 0,
        })),
        totalWeightedScore: 0,
        adminTotalWeightedScore: 0,
      };
    });
    setCategoryScores(reorderedScores);
  };

  const fetchCategoriesAndReorderScores = async (levelGroup: string, scores?: CategoryScore[], evaluationTypeId?: string) => {
    try {
      const catResponse = await categoryAPI.getCategoriesByLevelGroup(levelGroup, evaluationTypeId);
      console.log('categories for level group', levelGroup, ':', catResponse.data);
      setCategories(catResponse.data);

      const scoresToReorder = scores || categoryScores;
      if (scoresToReorder.length > 0) {
        reorderScoresToMatchCategories(catResponse.data, scoresToReorder);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load categories');
    }
  };

  const fetchEvaluation = async (evaluationId: string) => {
    try {
      console.log('[ADMIN EVAL] Fetching evaluation:', evaluationId);
      const response = await evaluationAPI.getEvaluationById(evaluationId);
      console.log('Evaluation data fetched from API:', response.data);
      const evalData = response.data;

      console.log('[ADMIN EVAL] Received evaluation data:', evalData);
      console.log('[ADMIN EVAL] userId type:', typeof evalData.userId);
      console.log('[ADMIN EVAL] userId value:', evalData.userId);

      // Check if userId is populated
      if (!evalData.userId || typeof evalData.userId === 'string') {
        console.error('[ADMIN EVAL] User not populated! userId is a string:', evalData.userId);
        setError('User data not loaded properly. Please try again.');
        return;
      }

      setEvaluation(evalData);
      dispatch(setCurrentEvaluation(evalData));

      // Normalize scores to ensure they are numbers and initialize adminScore if missing
      const normalizedScores = evalData.categoryScores.map((cat: CategoryScore) => ({
        ...cat,
        scores: cat.scores.map(s => ({
          ...s,
          score: typeof s.score === 'number' ? s.score : (s.score ? parseFloat(String(s.score)) : 1),
          adminScore: s.adminScore !== undefined
            ? (typeof s.adminScore === 'number' ? s.adminScore : parseFloat(String(s.adminScore)) || 0)
            : 0, // Initialize to 0 if adminScore doesn't exist
        })),
        totalWeightedScore: typeof cat.totalWeightedScore === 'number'
          ? cat.totalWeightedScore
          : (cat.totalWeightedScore ? parseFloat(String(cat.totalWeightedScore)) : 0),
        adminTotalWeightedScore: cat.adminTotalWeightedScore !== undefined
          ? (typeof cat.adminTotalWeightedScore === 'number' ? cat.adminTotalWeightedScore : parseFloat(String(cat.adminTotalWeightedScore)) || 0)
          : 0, // Initialize to 0 if adminTotalWeightedScore doesn't exist
      }));

      setCategoryScores(normalizedScores);
      setShowAdminScoresToEmployee(evalData.showAdminScoresToEmployee || false);
      setTaggedProjects(evalData.taggedProjects || []);

      // Initialize tracking for concurrent editing
      if (evalData.lastUpdatedBy === 'employee') {
        setLastKnownEmployeeUpdate(evalData.updatedAt);
      }

      // Fetch categories for the level group and reorder scores
      console.log('[ADMIN EVAL] Fetching categories for level group:', evalData.levelGroup);
      await fetchCategoriesAndReorderScores(evalData.levelGroup, normalizedScores, evalData.evaluationTypeId);
      console.log('[ADMIN EVAL] Categories fetched successfully');
    } catch (err: any) {
      console.error('[ADMIN EVAL] Error loading evaluation:', err);
      setError(err.response?.data?.message || 'Failed to load evaluation');
    }
  };

  const handleAdminScoreChange = (categoryIndex: number, subIndex: number, score: number) => {
    const newCategoryScores = [...categoryScores];

    // Guard clause: ensure the category and subcategory exist
    if (!newCategoryScores[categoryIndex] || !newCategoryScores[categoryIndex].scores[subIndex]) {
      console.log('[ADMIN EVAL] Invalid category or subcategory index', { categoryIndex, subIndex });
      console.log('CategoryScores:', newCategoryScores);
      console.log('Categories:', categories);
      console.log('Category Index:', categoryIndex, 'Sub Index:', subIndex);
      return;
    }

    // Double validation: ensure category and subcategory names match
    const categoryScore = newCategoryScores[categoryIndex];
    const category = categories[categoryIndex];
    const subcategoryScore = categoryScore.scores[subIndex];
    const subcategory = category.subCategories[subIndex];

    if (categoryScore.categoryName !== category.name) {
      console.error('[ADMIN EVAL] Category name mismatch!', {
        scoreCategory: categoryScore.categoryName,
        uiCategory: category.name,
        categoryIndex
      });
      return;
    }

    if (subcategoryScore.subCategoryName !== subcategory.name) {
      console.error('[ADMIN EVAL] Subcategory name mismatch!', {
        scoreSubcategory: subcategoryScore.subCategoryName,
        uiSubcategory: subcategory.name,
        categoryIndex,
        subIndex
      });
      return;
    }

    newCategoryScores[categoryIndex].scores[subIndex].adminScore = score;

    // Calculate admin weighted score for this category
    let totalAdminWeighted = 0;
    category.subCategories.forEach((sub, idx) => {
      const adminScore = newCategoryScores[categoryIndex].scores[idx]?.adminScore || 0;
      const weight = sub.weight ?? 0;

      // Ensure both are numbers before multiplication
      const numericScore = typeof adminScore === 'number' ? adminScore : parseFloat(String(adminScore)) || 0;
      const numericWeight = typeof weight === 'number' ? weight : parseFloat(String(weight)) || 0;

      if (numericScore > 0) {
        totalAdminWeighted += numericScore * numericWeight;
      }
    });

    // Ensure totalAdminWeighted is a proper number
    newCategoryScores[categoryIndex].adminTotalWeightedScore = isNaN(totalAdminWeighted) ? 0 : totalAdminWeighted;

    setCategoryScores(newCategoryScores);
  };

  const handleAdminCommentsChange = (categoryIndex: number, subIndex: number, comments: string) => {
    const newCategoryScores = [...categoryScores];

    // Guard clause: ensure the category and subcategory exist
    if (!newCategoryScores[categoryIndex] || !newCategoryScores[categoryIndex].scores[subIndex]) {
      console.error('[ADMIN EVAL] Invalid category or subcategory index for comments', { categoryIndex, subIndex });
      return;
    }

    // Double validation: ensure category and subcategory names match
    const categoryScore = newCategoryScores[categoryIndex];
    const category = categories[categoryIndex];
    const subcategoryScore = categoryScore.scores[subIndex];
    const subcategory = category.subCategories[subIndex];

    if (categoryScore.categoryName !== category.name) {
      console.error('[ADMIN EVAL] Category name mismatch in comments!', {
        scoreCategory: categoryScore.categoryName,
        uiCategory: category.name,
        categoryIndex
      });
      return;
    }

    if (subcategoryScore.subCategoryName !== subcategory.name) {
      console.error('[ADMIN EVAL] Subcategory name mismatch in comments!', {
        scoreSubcategory: subcategoryScore.subCategoryName,
        uiSubcategory: subcategory.name,
        categoryIndex,
        subIndex
      });
      return;
    }

    // Initialize adminComments if it doesn't exist
    if (!newCategoryScores[categoryIndex].scores[subIndex].adminComments) {
      newCategoryScores[categoryIndex].scores[subIndex].adminComments = '';
    }

    newCategoryScores[categoryIndex].scores[subIndex].adminComments = comments;
    setCategoryScores(newCategoryScores);
  };

  const handleProjectScoreChange = (projectId: string, score: number) => {
    setTaggedProjects(taggedProjects.map(tp =>
      tp.projectId === projectId ? { ...tp, projectScore: score } : tp
    ));
  };

  const handleProjectCommentsChange = (projectId: string, comments: string) => {
    setTaggedProjects(taggedProjects.map(tp =>
      tp.projectId === projectId ? { ...tp, adminComments: comments } : tp
    ));
  };

  const handleProjectRelevanceChange = (projectId: string, relevance: string) => {
    setTaggedProjects(taggedProjects.map(tp =>
      tp.projectId === projectId ? { ...tp, relevance } : tp
    ));
  };

  const handleProjectContributionChange = (projectId: string, contribution: string) => {
    setTaggedProjects(taggedProjects.map(tp =>
      tp.projectId === projectId ? { ...tp, contribution } : tp
    ));
  };

  const calculateAdminTotalScore = () => {
    return categoryScores.reduce((sum, cat) => {
      const weightedScore = typeof cat.adminTotalWeightedScore === 'number'
        ? cat.adminTotalWeightedScore
        : parseFloat(String(cat.adminTotalWeightedScore)) || 0;
      return sum + (isNaN(weightedScore) ? 0 : weightedScore);
    }, 0);
  };

  const handleSaveAdminScores = async () => {
    if (!id) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('[ADMIN SCORES] Saving admin scores for evaluation:', id);
      console.log('[ADMIN SCORES] Category scores being sent:', categoryScores);
      console.log('[ADMIN SCORES] Categories order in UI:', categories.map(c => c.name));

      // Calculate admin performance rating
      const adminTotalScore = calculateAdminTotalScore();
      const adminPerformanceRating = adminTotalScore > 0
        ? adminTotalScore >= 4.5 ? 'Outstanding'
          : adminTotalScore >= 3.5 ? 'Exceeds Expectations'
            : adminTotalScore >= 2.5 ? 'Meets Expectations'
              : adminTotalScore >= 1.5 ? 'Needs Improvement'
                : 'Unsatisfactory'
        : undefined;

      console.log('[ADMIN SCORES] Admin total score:', adminTotalScore);
      console.log('[ADMIN SCORES] Admin performance rating:', adminPerformanceRating);

      const response = await evaluationAPI.updateAdminScores(id, {
        categoryScores,
        adminPerformanceRating,
        showAdminScoresToEmployee,
        taggedProjects,
      });

      console.log('[ADMIN SCORES] Response:', response.data);

      setSuccess('Admin scores saved successfully!');

      // Update Redux with the new evaluation data
      // Backend returns { message: '...', evaluation: {...} }
      const updatedEvaluation = response.data.evaluation || response.data;
      console.log('[ADMIN SCORES] Updated evaluation:', updatedEvaluation);
      console.log('[ADMIN SCORES] Response categoryScores order:', updatedEvaluation.categoryScores.map((c: any) => c.categoryName));

      dispatch(updateEvaluation(updatedEvaluation));
      setEvaluation(updatedEvaluation);

      // Normalize scores after update and preserve adminScores from current state if missing in response
      const normalizedScores = updatedEvaluation.categoryScores.map((cat: CategoryScore) => {
        // Find the corresponding category in current state to preserve adminScores
        const currentCategory = categoryScores.find(currentCat => currentCat.categoryName === cat.categoryName);

        return {
          ...cat,
          scores: cat.scores.map((s, index) => {
            // If adminScore is missing in response, try to get it from current state
            const currentSubScore = currentCategory?.scores[index];
            const adminScore = s.adminScore !== undefined
              ? (typeof s.adminScore === 'number' ? s.adminScore : parseFloat(String(s.adminScore)) || 0)
              : (currentSubScore?.adminScore !== undefined ? currentSubScore.adminScore : 0);

            return {
              ...s,
              score: typeof s.score === 'number' ? s.score : (s.score ? parseFloat(String(s.score)) : 1),
              adminScore,
            };
          }),
          totalWeightedScore: typeof cat.totalWeightedScore === 'number'
            ? cat.totalWeightedScore
            : (cat.totalWeightedScore ? parseFloat(String(cat.totalWeightedScore)) : 0),
          adminTotalWeightedScore: cat.adminTotalWeightedScore && typeof cat.adminTotalWeightedScore !== 'number'
            ? parseFloat(String(cat.adminTotalWeightedScore))
            : cat.adminTotalWeightedScore,
        };
      });

      // Reorder the response scores to match current categories order
      reorderScoresToMatchCategories(categories, normalizedScores);
      console.log('[ADMIN SCORES] ✓ Admin scores saved and state updated');
    } catch (err: any) {
      console.error('[ADMIN SCORES] Error saving admin scores:', err);
      setError(err.response?.data?.message || 'Failed to save admin scores');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProjectDetails = async (projectId: string) => {
    try {
      setLoadingProject(true);
      const response = await projectAPI.getProjectById(projectId);
      setSelectedProject(response.data.data);
      setShowProjectModal(true);
    } catch (err: any) {
      console.error('[PROJECT MODAL] Error fetching project:', err);
      setError(err.response?.data?.message || 'Failed to load project details');
    } finally {
      setLoadingProject(false);
    }
  };

  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setSelectedProject(null);
  };

  // Show error if there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-brand-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-card p-8">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-semibold">Error loading evaluation</p>
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-brand-gray-600 text-white rounded hover:bg-brand-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="min-h-screen bg-brand-gray-50 py-8">
        <div>
          <div className="bg-white rounded-xl shadow-card p-8">
            <p className="text-brand-gray-600">Loading evaluation...</p>
          </div>
        </div>
      </div>
    );
  }

  const user = evaluation.userId as User;
  const employeeTotalScore = evaluation.totalScore || 0;
  const adminTotalScore = calculateAdminTotalScore();

  // Add null check for user
  if (!user || !user.name) {
    return (
      <div className="min-h-screen bg-brand-gray-50 py-8">
        <div>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary-600 mx-auto mb-4"></div>
              <p className="text-brand-gray-600">Loading evaluation details...</p>
              <p className="text-xs text-brand-gray-500 mt-2">If this persists, user data may not be loaded.</p>
              <button
                onClick={() => {
                  console.log('[ADMIN EVAL] User data:', user);
                  console.log('[ADMIN EVAL] Evaluation:', evaluation);
                }}
                className="mt-4 px-3 py-1 text-xs bg-brand-gray-200 hover:bg-brand-gray-300 rounded"
              >
                Debug Info
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-brand-gray-900">Admin Review & Scoring</h1>
            <p className="text-brand-gray-600 mt-1">Review and add admin scores for {user.name}'s evaluation</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Real-time indicator */}
            <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              {isRefreshing ? (
                <>
                  <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-700">Refreshing...</span>
                </>
              ) : (
                <>
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">
                    Last updated: {lastRefreshed.toLocaleTimeString()}
                  </span>
                </>
              )}
            </div>

            {/* Auto-refresh toggle */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 mr-2"
              />
              <span className="text-sm text-gray-700">Auto-refresh</span>
            </label>

            {/* Manual refresh button */}
            <button
              onClick={refreshEvaluation}
              disabled={isRefreshing}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh now"
            >
              <svg className={`w-4 h-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 text-brand-gray-700 hover:text-brand-gray-900 font-medium transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {conflictDetected && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 13.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <span className="font-semibold">Employee Update Detected!</span>
              <p className="text-sm mt-1">
                The employee has made changes to their evaluation. The view has been automatically updated to show the latest scores and evidence.
              </p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-brand-gray-200 shadow-card">
            <p className="text-sm font-medium text-brand-gray-600 mb-2">Employee</p>
            <p className="text-2xl font-bold text-brand-gray-900">{user.name}</p>
            <p className="text-sm text-brand-gray-500 mt-1">{user.level} • {evaluation.levelGroup}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-6 text-white shadow-lg">
            <p className="text-blue-100 text-sm font-medium mb-2">Employee Score</p>
            <p className="text-4xl font-bold">{(employeeTotalScore ?? 0).toFixed(2)}</p>
            <p className="text-blue-100 text-sm mt-2">{evaluation.performanceRating}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-6 text-white shadow-lg">
            <p className="text-purple-100 text-sm font-medium mb-2">Admin Score</p>
            <p className="text-4xl font-bold">{(evaluation.adminTotalScore ?? adminTotalScore ?? 0).toFixed(2)}</p>
            <p className="text-purple-100 text-sm mt-2">{evaluation.adminPerformanceRating || 'Not Yet Rated'}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-brand-gray-200 shadow-card">
            <p className="text-sm font-medium text-brand-gray-600 mb-2">Period</p>
            <p className="text-xl font-bold text-brand-gray-900">{evaluation.evaluationPeriod}</p>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${evaluation.status === 'submitted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
              {evaluation.status}
            </span>
          </div>
        </div>

        {/* Real-time Updates Info */}
        <div className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-green-900 mb-1">Real-time Updates</h3>
              <p className="text-xs text-green-700">
                Enable auto-refresh to automatically update employee scores and evidence every 10 seconds.
                Your admin scores and comments are always preserved during updates.
                {evaluation?.lastUpdatedBy === 'employee' && evaluation?.updatedAt && (
                  <span className="block mt-1 font-medium">
                    Last employee update: {new Date(evaluation.updatedAt).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {evaluation?.lastUpdatedBy === 'employee' && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                  Employee Active
                </span>
              )}
              {autoRefreshEnabled && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                  Live Updates
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Admin Score Visibility Toggle */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showAdminScoresToEmployee}
              onChange={(e) => setShowAdminScoresToEmployee(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <div className="ml-3">
              <span className="text-sm font-semibold text-blue-900">
                Show admin scores to employee
              </span>
              <p className="text-xs text-blue-700 mt-1">
                When enabled, the employee can see your admin scores and comments in their evaluation view
              </p>
            </div>
          </label>
        </div>

        {/* Categories and Scores */}
        <div className="space-y-6">
          {categories.map((category, catIndex) => (
            <div key={category._id} className="bg-white rounded-xl border border-brand-gray-200 shadow-card overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-brand-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-brand-gray-900">
                      {category.name}
                    </h3>
                    <p className="text-sm text-brand-gray-600 mt-1">
                      Weight: {((category.weight ?? 0) * 100).toFixed(0)}% •
                      Employee Weighted: {(categoryScores[catIndex]?.totalWeightedScore ?? 0).toFixed(2)} •
                      Admin Weighted: {(categoryScores[catIndex]?.adminTotalWeightedScore ?? 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-brand-gray-200">
                    <thead>
                      <tr className="bg-brand-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-brand-gray-700 uppercase tracking-wider">
                          Sub-Category
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-gray-700 uppercase tracking-wider w-32">
                          Employee Score
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-brand-gray-700 uppercase tracking-wider w-32">
                          Admin Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-brand-gray-700 uppercase tracking-wider">
                          Employee Evidence
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-brand-gray-700 uppercase tracking-wider">
                          Admin Comments
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-brand-gray-200">
                      {category.subCategories.map((subCat, subIndex) => {
                        const score = categoryScores[catIndex]?.scores[subIndex];
                        return (
                          <tr key={subIndex} className="hover:bg-brand-gray-50 transition-colors">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-brand-gray-900">
                              {subCat.name}
                              <span className="text-xs text-brand-gray-500 block mt-1">
                                Weight: {((subCat.weight ?? 0) * 100).toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="inline-flex items-center justify-center h-10 w-16 bg-blue-100 text-blue-800 font-bold rounded-lg">
                                {score?.score || 0}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <select
                                value={score?.adminScore || ''}
                                onChange={(e) => handleAdminScoreChange(catIndex, subIndex, Number(e.target.value))}
                                className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent font-semibold text-center"
                              >
                                <option value="">--</option>
                                {[1, 2, 3, 4, 5].map(num => (
                                  <option key={num} value={num}>{num}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm text-brand-gray-700 max-w-md">
                                {score?.evidence || <span className="text-brand-gray-400 italic">No evidence provided</span>}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <textarea
                                value={score?.adminComments || ''}
                                onChange={(e) => handleAdminCommentsChange(catIndex, subIndex, e.target.value)}
                                placeholder="Add your comments..."
                                rows={2}
                                className="w-full px-3 py-2 border border-brand-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tagged Projects Section */}
        {taggedProjects.length > 0 && (
          <div className="mt-8">
            <div className="bg-white rounded-xl border-2 border-brand-gray-200 shadow-card overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Employee's Attached Projects
                </h3>
                <p className="text-indigo-100 text-sm mt-1">Review and score the projects attached by the employee</p>
              </div>

              <div className="p-6 space-y-6">
                {taggedProjects.map((project) => (
                  <div key={project.projectId} className="border-2 border-brand-gray-200 rounded-xl p-5 bg-gradient-to-br from-white to-gray-50 hover:shadow-lg transition-shadow">
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-brand-gray-900 mb-1">{project.projectName}</h4>
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => handleViewProjectDetails(project.projectId)}
                              disabled={loadingProject}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {loadingProject ? 'Loading...' : 'View Project Details'}
                            </button>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-right">
                            <span className="text-sm font-medium text-brand-gray-600">Project Score</span>
                            <div className="flex items-center space-x-2 mt-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={project.projectScore || ''}
                                onChange={(e) => handleProjectScoreChange(project.projectId, Number(e.target.value))}
                                placeholder="0-100"
                                className="w-20 px-3 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold text-purple-700"
                              />
                              <span className="text-brand-gray-600 font-medium">/ 100</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Employee's Contributions - Editable */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 block">
                            Why This Project is Relevant
                          </label>
                          <textarea
                            value={project.relevance || ''}
                            onChange={(e) => handleProjectRelevanceChange(project.projectId, e.target.value)}
                            placeholder="Describe why this project is relevant to the evaluation..."
                            rows={3}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none bg-white"
                          />
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <label className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 block">
                            Employee's Contribution
                          </label>
                          <textarea
                            value={project.contribution || ''}
                            onChange={(e) => handleProjectContributionChange(project.projectId, e.target.value)}
                            placeholder="Detail the employee's specific contributions to this project..."
                            rows={3}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none bg-white"
                          />
                        </div>
                      </div>

                      {/* Admin Comments */}
                      <div>
                        <label className="block text-sm font-semibold text-brand-gray-700 mb-2">
                          <svg className="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                          Admin Feedback on This Project
                        </label>
                        <textarea
                          value={project.adminComments || ''}
                          onChange={(e) => handleProjectCommentsChange(project.projectId, e.target.value)}
                          placeholder="Provide feedback on the employee's work on this project..."
                          rows={3}
                          className="w-full px-4 py-3 border-2 border-brand-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-6 py-3 bg-white border-2 border-brand-gray-300 text-brand-gray-700 font-semibold rounded-lg hover:bg-brand-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAdminScores}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 transition-all shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Admin Scores'
            )}
          </button>
        </div>

        {/* Project Details Modal */}
        {showProjectModal && selectedProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-brand-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-brand-gray-900">Project Details</h2>
                <button
                  onClick={handleCloseProjectModal}
                  className="text-brand-gray-500 hover:text-brand-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Project Header */}
                <div>
                  <h3 className="text-2xl font-bold text-brand-gray-900 mb-2">{selectedProject.name}</h3>
                  <p className="text-brand-gray-600">{selectedProject.description}</p>

                  <div className="flex flex-wrap gap-3 mt-4">
                    <span className={`px-4 py-2 text-sm font-semibold rounded-full ${selectedProject.status === 'production' || selectedProject.status === 'done' ? 'bg-green-100 text-green-700' :
                        selectedProject.status === 'development' ? 'bg-blue-100 text-blue-700' :
                          selectedProject.status === 'testing' || selectedProject.status === 'uat' || selectedProject.status === 'sit' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                      }`}>
                      {selectedProject.status.toUpperCase()}
                    </span>
                    <span className={`px-4 py-2 text-sm font-semibold rounded-full ${selectedProject.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        selectedProject.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          selectedProject.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                      }`}>
                      {selectedProject.priority.toUpperCase()} PRIORITY
                    </span>
                    {selectedProject.category && (
                      <span className="px-4 py-2 text-sm font-semibold bg-purple-100 text-purple-700 rounded-full">
                        {selectedProject.category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Project Stats */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-brand-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-brand-gray-600 mb-1">Tasks</p>
                    <p className="text-xl font-bold text-brand-gray-900">
                      {selectedProject.stats.completedTasks}/{selectedProject.stats.totalTasks}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray-600 mb-1">Comments</p>
                    <p className="text-xl font-bold text-brand-gray-900">{selectedProject.stats.totalComments}</p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray-600 mb-1">Team Size</p>
                    <p className="text-xl font-bold text-brand-gray-900">{selectedProject.members.length + 1}</p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray-600 mb-1">Views</p>
                    <p className="text-xl font-bold text-brand-gray-900">{selectedProject.stats.viewCount}</p>
                  </div>
                </div>

                {/* Project Owner */}
                <div className="border border-brand-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-brand-gray-900 mb-3">Project Owner</h4>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {selectedProject.owner.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-brand-gray-900">{selectedProject.owner.name}</p>
                      <p className="text-sm text-brand-gray-600">{selectedProject.owner.email}</p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                {(selectedProject.dates.startDate || selectedProject.dates.estimatedEndDate) && (
                  <div className="border border-brand-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-brand-gray-900 mb-3">Timeline</h4>
                    <div className="space-y-2">
                      {selectedProject.dates.startDate && (
                        <div className="flex justify-between">
                          <span className="text-brand-gray-600">Start Date:</span>
                          <span className="font-medium text-brand-gray-900">
                            {new Date(selectedProject.dates.startDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {selectedProject.dates.estimatedEndDate && (
                        <div className="flex justify-between">
                          <span className="text-brand-gray-600">Est. End Date:</span>
                          <span className="font-medium text-brand-gray-900">
                            {new Date(selectedProject.dates.estimatedEndDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {selectedProject.dates.actualEndDate && (
                        <div className="flex justify-between">
                          <span className="text-brand-gray-600">Actual End Date:</span>
                          <span className="font-medium text-brand-gray-900">
                            {new Date(selectedProject.dates.actualEndDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Team Members */}
                {selectedProject.members.length > 0 && (
                  <div className="border border-brand-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-brand-gray-900 mb-3">Team Members</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedProject.members.map((member, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-brand-gray-50 rounded-lg">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-brand-gray-900">{member.name}</p>
                            <p className="text-xs text-brand-gray-600">{member.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedProject.tags.length > 0 && (
                  <div className="border border-brand-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-brand-gray-900 mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tags.map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-brand-gray-100 text-brand-gray-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Review */}
                {selectedProject.adminReview && selectedProject.adminReview.score !== undefined && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Admin Review
                    </h4>
                    <div className="flex items-center gap-4 mb-2">
                      <div>
                        <p className="text-sm text-blue-700">Score</p>
                        <p className="text-3xl font-bold text-blue-900">{selectedProject.adminReview.score}/100</p>
                      </div>
                      {selectedProject.adminReview.approved !== undefined && (
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${selectedProject.adminReview.approved
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                          }`}>
                          {selectedProject.adminReview.approved ? 'Approved' : 'Not Approved'}
                        </span>
                      )}
                    </div>
                    {selectedProject.adminReview.feedback && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-sm font-medium text-blue-900 mb-1">Feedback:</p>
                        <p className="text-sm text-blue-800">{selectedProject.adminReview.feedback}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-brand-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseProjectModal}
                  className="w-full px-6 py-3 bg-brand-gray-600 text-white font-semibold rounded-lg hover:bg-brand-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditEvaluationWithAdminScore;

